<script lang="ts" setup>
import PageHeader from '@/components/header/PageHeader.vue'
import { BusEvent, ConfigInputType, ConfigSchemaItem, ValidationRuleType } from '@/helper/Types'
import ConfigElement from '@/components/admin/ConfigElement.vue'
import { camelize, validateComponent, ValidatorType } from '@/helper/ValidateDirective'
import { api } from '@/services/ApiService'
import bus from '@/helper/Bus'
import { ModalTypes } from '@/helper/ModalTypes'
import SectionIntersectionView from '@/helper/SectionIntersectionView'
import { NavigationGuardNext, onBeforeRouteLeave, RouteLocationNormalized, useRoute, useRouter } from 'vue-router'
import userStore from '@/store/UserStore'
import { ComponentInternalInstance, computed, onMounted, onUnmounted, reactive, ref } from 'vue'
import { setMetaTitle } from '@/helper/tools'

const COMPONENT_NAME = 'ConfigElement'

const router = useRouter()
const route = useRoute()
const country = route.params.countryCode.toString() || ''
const brand = route.params.brand.toString() || ''
const validator: ValidatorType = reactive({})
const configElementComponent = ref()

let jsonSchema!: any
const config = ref<Record<string, any>>({})
let originalConfigStr = ''
const configSchema = ref<any[]>([])
// eslint-disable-next-line @typescript-eslint/no-unused-vars
let intersector!: SectionIntersectionView

const sections = computed((): string[] => {
  return configSchema.value.map(section => section.title)
})

const camelizedSections = computed((): string[] => {
  return configSchema.value.map(section => camelize(section.title))
})

bus.$onC(COMPONENT_NAME, BusEvent.CONFIG_DELETE_CONFIRMED, deleteConfirmed)
bus.$onC(COMPONENT_NAME, BusEvent.CONFIG_DISABLE_CONFIRMED, disableConfirmed)
bus.$onC(COMPONENT_NAME, BusEvent.CONFIG_KEY_CHANGE_WARNING, editConfirmed)

onBeforeRouteLeave((to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
  let allowNav = true
  if (hasChanges()) {
    allowNav = confirm('There are UNSAVED changes. Leave page?')
  }
  next(allowNav)
})

onMounted(async () => {
  await loadData()
  intersector = new SectionIntersectionView(camelizedSections.value)
  setMetaTitle(`Admin config - configuration for ${brand} / ${country}`)
})

onUnmounted(async () => {
  bus.$offC(COMPONENT_NAME)
})

function hasChanges(): boolean {
  return originalConfigStr !== JSON.stringify(config.value)
}

function createConfigItems() {
  configSchema.value.forEach(el => {
    if (!el?.key) {
      return
    }
    if (config.value[el.key]) {
      return
    }
    config.value[el.key] = configElementComponent.value?.createEmptyItem(el)
  })
}

async function loadData() {
  try {
    jsonSchema = (await api.loadConfigSchema()).properties
    config.value = await api.loadAdminConfig(brand, country)
  } catch (e) {
    router.push({ name: 'ProjectOverviewPage' }).catch(() => '')
    return
  }

  setUpConfigSchema()
  createConfigItems()

  // save config after waiting for the renderer (inner component changes data)
  setTimeout(() => (originalConfigStr = JSON.stringify(config.value)), 500)
}

function resetBtnClicked() {
  loadData()
}

function deleteClicked(prop: unknown, key: string, index: number): void {
  bus.$emit(BusEvent.SHOW_MODAL, {
    modalType: ModalTypes.CONFIG_DELETION_WARNING,
    confirmEventName: BusEvent.CONFIG_DELETE_CONFIRMED,
    payload: { prop: prop, key: key, index: index }
  })
}

function deleteConfirmed(payload: Record<string, any>): void {
  const { prop, key, index } = payload
  prop[key].splice(index, 1)
}

function disableClicked(prop: unknown, key: string): void {
  bus.$emit(BusEvent.SHOW_MODAL, {
    modalType: ModalTypes.CONFIG_DISABLING_WARNING,
    confirmEventName: BusEvent.CONFIG_DISABLE_CONFIRMED,
    payload: { prop: prop, key: key }
  })
}

function disableConfirmed(payload: Record<string, any>): void {
  const { prop, key } = payload
  prop[key] = false
}

function editClicked(configElement: ComponentInternalInstance): void {
  bus.$emit(BusEvent.SHOW_MODAL, {
    modalType: ModalTypes.CONFIG_KEY_CHANGE_WARNING,
    confirmEventName: BusEvent.CONFIG_KEY_CHANGE_WARNING,
    payload: configElement
  })
}

function editConfirmed(configElement: ComponentInternalInstance): void {
  (configElement as InstanceType<typeof ConfigElement>).exposed.readonly.value = false
}

function sectionNavItemClicked(section: string) {
  const offset = 16 // 16 adds a small margin
  const scrollTop =
    (document.getElementById(`section-${camelize(section)}`)?.getBoundingClientRect()?.top || 0) +
    window.scrollY -
    offset
  window.scrollTo({
    top: scrollTop,
    behavior: 'smooth'
  })
}

// returns TRUE if node is NOT empty
function removeEmptyConfigValues(node: any, lvl = 0): boolean {
  if (Array.isArray(node)) {
    if (!node.length) {
      return false
    }

    const deleteIdxs: number[] = []

    node.forEach((child, idx) => {
      if (removeEmptyConfigValues(child, lvl + 1)) {
        return
      }

      deleteIdxs.push(idx)
    })

    deleteIdxs.reverse()
    deleteIdxs.forEach(idx => node.splice(idx, 1))

    return !!node.length
  }

  if (typeof node === 'object') {
    const keys = Object.keys(node)

    if (!keys.length) {
      return false
    }

    keys.forEach(key => {
      if (!removeEmptyConfigValues(node[key], lvl + 1)) {
        delete node[key]
      }
    })

    return !!Object.keys(node).length
  }

  return node !== undefined && node !== null && node !== ''
}

async function saveBtnClicked() {
  if (!validateComponent(validator)) {
    bus.$emit(BusEvent.SCROLL_TO_FIRST_ERROR)
    return
  }

  removeEmptyConfigValues(config.value)

  try {
    await api.updateAdminConfig(brand, country, config.value)
    userStore.setConfig(await api.loadConfiguration())

    alert('Data successfully saved!')
    originalConfigStr = JSON.stringify(config.value)
  } catch {
    alert('Failed to save data! Please check the data')
  }
}

function setUpConfigSchema() {
  configSchema.value = [
    {
      title: 'Feature toggles',
      items: [
        {
          type: ConfigInputType.TOGGLE,
          label: 'Use OpenMeteo',
          description: 'Use OpenMeteo based outdoor temperatures',
          key: 'enableOpenMeteo'
        },
        {
          type: ConfigInputType.TOGGLE,
          label: 'Enable simulation',
          description: 'Enable the simulation.',
          key: 'enableSimulation'
        },
        {
          type: ConfigInputType.TOGGLE,
          label: 'Enable city dropdown',
          description: 'A dropdown will be used instead of the input field to choose the city correlating to the postalcode.',
          key: 'enableCityDropdown'
        },
        {
          type: ConfigInputType.TOGGLE,
          label: 'Enable heat pump placement',
          key: 'enableHeatPumpPlacement',
          description: 'Shows heat pump placement on installation step and enables noise calculation.'
        },
        {
          type: ConfigInputType.TOGGLE,
          label: 'Optional backup heater',
          key: 'backupHeaterOptional',
          description: 'Shows "include backup heater" checkbox above backup heaters.'
        },
        {
          type: ConfigInputType.TOGGLE,
          label: 'Enable customer address',
          key: 'enableCustomerAddress',
          description: 'Allows to save a separate address for the customer.'
        },
        {
          type: ConfigInputType.TOGGLE,
          label: 'Enable heating cost comparison',
          key: 'enableHeatingCostComparison',
          description: 'Enables savings page in report and energy prices on download page.'
        },
        {
          type: ConfigInputType.TOGGLE,
          label: 'Enable altitude input in project details',
          key: 'enableAltitudeInput',
          description: 'Shows input on project details and uses altitute to determine outdoor temperature.'
        },
        {
          type: ConfigInputType.TOGGLE,
          label: 'Enable heat pump voltage filter',
          key: 'enableHeatpumpVoltageFilter',
          description: 'Shows the voltage filter on heat pump step.'
        },
        {
          type: ConfigInputType.TOGGLE,
          label: 'Enable irregular currency',
          key: 'enableIrregularCurrency',
          description: 'Disables conversion from fractional currency.'
        },
        {
          type: ConfigInputType.TOGGLE,
          label: 'Recommended products on top',
          key: 'recommendationsOnTop',
          description: 'Recommended articles get sorted to the top.'
        },
        {
          type: ConfigInputType.TOGGLE,
          label: 'Enable accessory short list',
          key: 'enableAccessoryShortList',
          description: 'When enabled collapse and hide accessories with quantity 0 on accessory step.'
        },
        {
          type: ConfigInputType.TOGGLE,
          label: 'Enable sales set selection',
          key: 'enableBundleSelection',
          description: 'When enabled sale sets with price will be visible'
        },
      ]
    },
    {
      title: 'Report feature toggles',
      items: [
        {
          type: ConfigInputType.TOGGLE,
          label: 'Enable PDF footer text',
          key: 'enablePdfFooterText',
          description: 'Shows installer/customer name and last modified date in the report footer.'
        },
        {
          type: ConfigInputType.TOGGLE,
          label: 'Enable PDF installer contact data',
          key: 'enablePdfInstallerContactData',
          description: 'When enabled shows installer address on cover page otherwise the customer address.'
        },
        {
          type: ConfigInputType.TOGGLE,
          label: 'Enable extended noise calculation',
          key: 'enableExtendedNoiseCalculation',
          description: 'Adds another page to the report containing a detailed noise calculation at different RPMs (requires acoustic properties to be set on the heat pump).'
        },
        {
          type: ConfigInputType.TOGGLE,
          label: 'Enable subsidies page in PDF report',
          key: 'enablePdfSubsidiesPage',
          description: 'Adds subsidies page to report (relevant for France).'
        },
      ]
    },
    {
      title: 'Prices',
      items: [
        {
          type: ConfigInputType.TOGGLE,
          label: 'Enable VAT prices in the application',
          key: 'enableDisplayOfVatPrices',
          description: 'Shows all prices including VAT within the application (not in the Report).'
        },
        {
          type: ConfigInputType.TOGGLE,
          label: 'Enable custom prices',
          key: 'enableCustomPrices',
          description: 'Allows setting custom prices for articles/packages on summary.'
        },
        {
          type: ConfigInputType.TOGGLE,
          label: 'Hide prices',
          key: 'hidePrices',
          description: 'Hides prices on summary and report.'
        },
        {
          type: ConfigInputType.TOGGLE,
          label: 'Enable show prices option on download page',
          key: 'enableShowPricesInReportOption',
          description: 'Allows the installer to toggle prices in the report.'
        },
        {
          type: ConfigInputType.TEXT,
          label: 'VAT rate (%)',
          key: 'vatRate',
          rules: () => ({ [ValidationRuleType.POSITIVE_DECIMAL]: true })
        },
      ]
    },
    {
      title: 'Heating',
      key: 'heatingLoadCalculation',
      items: [
        {
          type: ConfigInputType.TOGGLE,
          label: 'Enable heating load calculation',
          key: '@enableHeatingLoadCalculation',
          description: 'Enables the heating load calculation buttons'
        },
        {
          type: ConfigInputType.TOGGLE,
          label: 'Enable heating load calculation info',
          key: '@enableHeatingLoadCalculationInfo',
          description: 'Displays an info button to show a modal on click'
        },
        {
          type: ConfigInputType.TOGGLE,
          label: 'Enable heating load consumption calculation',
          key: '@enableHeatingLoadConsumptionCalculation',
          description: 'Enable the heating load consumption calculation button and form'
        },
        {
          type: ConfigInputType.TOGGLE,
          label: 'Enable heating load thermal envelope calculation',
          key: '@enableHeatingLoadThermalEnvelopeCalculation',
          description: 'Enable the heating load thermal envelope calculation button and form'
        },
        {
          type: ConfigInputType.TOGGLE,
          label: 'Enable living area input field',
          key: '@enableLivingArea',
          description: 'Show the heat loss living area input field on planning step'
        },
        {
          type: ConfigInputType.OBJECT,
          label: 'Window type',
          key: 'windowTypes',
          expandable: true,
          rules: () => (config.value.enableHeatingLoadCalculation ? { [ValidationRuleType.REQUIRED]: true } : {}),
          items: [
            {
              type: ConfigInputType.TEXT,
              label: 'Window type',
              key: 'windowType',
              rules: () => ({
                [ValidationRuleType.REQUIRED]: true,
                [ValidationRuleType.UNIQUE]: {
                  list: config.value.heatingLoadCalculation.windowTypes || [],
                  key: 'windowType'
                }
              }),
              isUnique: true
            },
            {
              type: ConfigInputType.TEXT,
              label: 'U-Value',
              key: 'uValue',
              rules: [ValidationRuleType.REQUIRED, ValidationRuleType.POSITIVE_DECIMAL]
            }
          ]
        },
        {
          type: ConfigInputType.TEXT,
          label: 'Ground temperature',
          key: 'groundTemperature',
          rules: () =>
            config.value.enableHeatingLoadCalculation
              ? { [ValidationRuleType.REQUIRED]: true, [ValidationRuleType.DECIMAL]: true }
              : { [ValidationRuleType.DECIMAL]: true }
        },
        {
          type: ConfigInputType.TEXT,
          label: 'Indoor temperature',
          key: 'indoorTemperatures',
          expandable: true,
          rules: () =>
            config.value.enableHeatingLoadCalculation
              ? { [ValidationRuleType.REQUIRED]: true, [ValidationRuleType.INTEGER]: true, [ValidationRuleType.MIN_VALUE]: [0] }
              : { [ValidationRuleType.INTEGER]: true, [ValidationRuleType.MIN_VALUE]: [0] }
        },
        {
          type: ConfigInputType.OBJECT,
          label: 'Ventilation type',
          key: 'ventilationTypes',
          expandable: true,
          rules: () => (config.value.enableHeatingLoadCalculation ? { [ValidationRuleType.REQUIRED]: true } : {}),

          items: [
            {
              type: ConfigInputType.SELECT,
              isUnique: true,
              label: 'Ventilation type',
              key: 'ventilationType',
              options:
              jsonSchema?.heatingLoadCalculation?.properties?.ventilationTypes?.items?.properties?.ventilationType
                ?.enum,
              rules: () => (config.value.enableHeatingLoadCalculation ? { [ValidationRuleType.REQUIRED]: true } : {})
            },
            {
              type: ConfigInputType.TEXT,
              label: 'Air exchange rate',
              key: 'airExchangeRate',
              rules: () =>
                config.value.enableHeatingLoadCalculation
                  ? { [ValidationRuleType.REQUIRED]: true, [ValidationRuleType.POSITIVE_DECIMAL]: true }
                  : { [ValidationRuleType.POSITIVE_DECIMAL]: true }
            }
          ]
        },
        {
          type: ConfigInputType.TEXT,
          label: 'Adjoined side temperature',
          key: 'adjoinedSideTemperature',
          rules: () =>
            config.value.enableHeatingLoadCalculation
              ? { [ValidationRuleType.REQUIRED]: true, [ValidationRuleType.POSITIVE_DECIMAL]: true }
              : { [ValidationRuleType.POSITIVE_DECIMAL]: true }
        },
        {
          type: ConfigInputType.TEXT,
          label: 'Window surface factor',
          key: 'windowSurfaceFactor',
          rules: () =>
            config.value.enableHeatingLoadCalculation
              ? { [ValidationRuleType.REQUIRED]: true, [ValidationRuleType.POSITIVE_DECIMAL]: true }
              : { [ValidationRuleType.POSITIVE_DECIMAL]: true }
        }
      ]
    },
    {
      title: 'Cooling',
      key: 'coolingLoadCalculation',
      items: [
        {
          type: ConfigInputType.TOGGLE,
          label: 'Enable cooling',
          key: '@enableCooling',
          description: 'Enable cooling selection button',
        },
        {
          type: ConfigInputType.TOGGLE,
          label: 'Enable cooling load calculation',
          key: '@enableCoolingLoadCalculation',
          description: 'Enables the cooling load calculation buttons',
        },
        {
          type: ConfigInputType.TOGGLE,
          label: 'Enable cooling load calculation info',
          key: '@enableCoolingLoadCalculationInfo',
          description: 'Displays an info button to show a modal on click',
        },
        {
          type: ConfigInputType.TOGGLE,
          label: 'Enable cooling simulation',
          key: '@enableCoolingSimulation',
          description: 'Enables the simulation of a cooling system',
        },
        {
          type: ConfigInputType.TOGGLE,
          label: 'Enable cooling page',
          key: '@enableCoolingPage',
          description: 'Adds a cooling page to the PDF report with SEER',
        },
        {
          type: ConfigInputType.OBJECT,
          label: 'Window orientation',
          key: 'windowOrientations',
          expandable: true,
          rules: () => (config.value.enableCoolingLoadCalculation ? { [ValidationRuleType.REQUIRED]: true } : {}),

          items: [
            {
              type: ConfigInputType.SELECT,
              isUnique: true,
              label: 'Window orientation',
              key: 'windowOrientation',
              options:
              jsonSchema?.coolingLoadCalculation?.properties?.windowOrientations?.items?.properties?.windowOrientation
                ?.enum,
              rules: () => (config.value.enableCoolingLoadCalculation ? { [ValidationRuleType.REQUIRED]: true } : {})
            },
            {
              type: ConfigInputType.TEXT,
              label: 'Radiation',
              key: 'radiation',
              rules: () =>
                config.value.enableCoolingLoadCalculation
                  ? { [ValidationRuleType.REQUIRED]: true, [ValidationRuleType.POSITIVE_DECIMAL]: true }
                  : { [ValidationRuleType.POSITIVE_DECIMAL]: true }
            }
          ]
        },
        {
          type: ConfigInputType.OBJECT,
          label: 'Ventilation type',
          key: 'ventilationTypes',
          expandable: true,
          rules: () => (config.value.enableCoolingLoadCalculation ? { [ValidationRuleType.REQUIRED]: true } : {}),

          items: [
            {
              type: ConfigInputType.SELECT,
              isUnique: true,
              label: 'Ventilation type',
              key: 'ventilationType',
              options:
              jsonSchema?.coolingLoadCalculation?.properties?.ventilationTypes?.items?.properties?.ventilationType
                ?.enum,
              rules: () => (config.value.enableCoolingLoadCalculation ? { [ValidationRuleType.REQUIRED]: true } : {})
            },
            {
              type: ConfigInputType.TEXT,
              label: 'Air exchange rate',
              key: 'airExchangeRate',
              rules: () =>
                config.value.enableCoolingLoadCalculation
                  ? { [ValidationRuleType.REQUIRED]: true, [ValidationRuleType.POSITIVE_DECIMAL]: true }
                  : { [ValidationRuleType.POSITIVE_DECIMAL]: true }
            }
          ]
        },
        {
          type: ConfigInputType.TEXT,
          label: 'Indoor temperature',
          key: 'indoorTemperatures',
          expandable: true,
          rules: () =>
            config.value.enableCoolingLoadCalculation
              ? { [ValidationRuleType.REQUIRED]: true, [ValidationRuleType.INTEGER]: true, [ValidationRuleType.MIN_VALUE]: [0] }
              : { [ValidationRuleType.INTEGER]: true, [ValidationRuleType.MIN_VALUE]: [0] }
        },
        {
          type: ConfigInputType.TEXT,
          label: 'Ventilation cooling load',
          key: 'ventilationCoolingLoad',
          rules: () =>
            config.value.enableCoolingLoadCalculation
              ? { [ValidationRuleType.REQUIRED]: true, [ValidationRuleType.POSITIVE_DECIMAL]: true }
              : { [ValidationRuleType.POSITIVE_DECIMAL]: true }
        },
        {
          type: ConfigInputType.TEXT,
          label: 'Internal cooling load for building dimensions',
          description: 'Used when building dimensions are provided instead of living space',
          key: 'internalCoolingLoad',
          rules: () =>
            config.value.enableCoolingLoadCalculation
              ? { [ValidationRuleType.REQUIRED]: true, [ValidationRuleType.POSITIVE_DECIMAL]: true }
              : { [ValidationRuleType.POSITIVE_DECIMAL]: true }
        },
        {
          type: ConfigInputType.TEXT,
          label: 'Internal cooling load for living space',
          description: 'Used when living space is provided instead of building dimensions. If not set internalCoolingLoad is used',
          key: 'internalCoolingLoadLivingSpace',
          rules: () =>
            config.value.enableCoolingLoadCalculation
              ? { [ValidationRuleType.REQUIRED]: true, [ValidationRuleType.POSITIVE_DECIMAL]: true }
              : { [ValidationRuleType.POSITIVE_DECIMAL]: true }
        },
        {
          type: ConfigInputType.TEXT,
          label: 'Cooling fallback max outdoor temperature',
          key: '@coolingFallbackMaxOutdoorTemperature',
          rules: ValidationRuleType.DECIMAL
        }
      ]
    },
    {
      title: 'Domestic hot water',
      items: [
        {
          type: ConfigInputType.TOGGLE,
          label: 'Suggest mixing pump groups for DHW',
          description: 'Suggest mixing pump groups to avoid timely limited increased temperature of heating elements after DHW production although heating circuit configuration does not demand a mixed HC',
          key: 'suggestMixingPumpGroupsForDhw'
        },
        {
          type: ConfigInputType.TEXT,
          label: 'DHW value',
          key: 'dhwValues',
          expandable: true,
          rules: ValidationRuleType.POSITIVE_DECIMAL
        },
        {
          type: ConfigInputType.TEXT,
          label: 'DHW min. suggestion factor',
          key: 'dhwMinSuggestionFactor',
          rules: [ValidationRuleType.REQUIRED, ValidationRuleType.POSITIVE_DECIMAL]
        },
        {
          type: ConfigInputType.TEXT,
          label: 'DHW max. suggestion factor',
          key: 'dhwMaxSuggestionFactor',
          rules: ValidationRuleType.POSITIVE_DECIMAL
        },
        {
          type: ConfigInputType.TEXT,
          label: 'DHW mixing loss (used for DHW recommendation)',
          key: 'dhwMixingLoss',
          rules: ValidationRuleType.POSITIVE_DECIMAL
        },
        {
          type: ConfigInputType.TEXT,
          label: 'DHW tapping temperature (simulation)',
          key: 'dhwTappingTemperature',
          rules: ValidationRuleType.POSITIVE_DECIMAL
        },
      ]
    },
    {
      title: 'Efficiency calculation',
      items: [
        {
          type: ConfigInputType.TEXT,
          label: 'Cold water temperature',
          key: 'dhwColdWaterTemperature',
          rules: ValidationRuleType.POSITIVE_DECIMAL
        },
        {
          type: ConfigInputType.TEXT,
          label: 'DHW usage rate',
          key: 'dhwUsageRate',
          rules: ValidationRuleType.POSITIVE_DECIMAL
        },
        {
          type: ConfigInputType.TEXT,
          label: 'Backup heater COP',
          key: 'backupHeaterCop',
          rules: ValidationRuleType.POSITIVE_DECIMAL
        },
        {
          type: ConfigInputType.TEXT,
          label: 'Years to calculate in HP eff. Excel',
          key: 'hpEfficiencyYearsToCalculate',
          rules: () => ({ [ValidationRuleType.REQUIRED]: true, [ValidationRuleType.INTEGER]: true, [ValidationRuleType.MIN_VALUE]: [1] })
        },
      ]
    },
    {
      title: 'Buffer tanks',
      items: [
        {
          type: ConfigInputType.TOGGLE,
          label: 'Optional buffer tank',
          key: 'bufferTankOptional',
          description: 'Adds a checkbox to the buffer tank section to allow it being marked as optional'
        },
        {
          type: ConfigInputType.TOGGLE,
          label: 'Separate heating and cooling buffer',
          key: 'enableBufferSeparation',
          description: 'Adds two buttons to the buffer tank section to allow separation of heating and cooling buffer tanks'
        },
        {
          type: ConfigInputType.TOGGLE,
          label: 'Display buffer min volume notifications',
          key: 'enableBufferMinVolumeHint',
          description: ''
        },
        {
          type: ConfigInputType.TEXT,
          label: 'Min. cooling buffer volume per kW (in litres)',
          key: 'minCoolingBufferVolumePerKw',
          rules: ValidationRuleType.POSITIVE_ZERO_DECIMAL
        },
        {
          type: ConfigInputType.TEXT,
          label: 'Buffer volume min. suggestion factor',
          key: 'bufferVolumeMinSuggestionFactor',
          rules: ValidationRuleType.POSITIVE_ZERO_DECIMAL
        },
        {
          type: ConfigInputType.TEXT,
          label: 'Buffer volume max. suggestion factor',
          key: 'bufferVolumeMaxSuggestionFactor',
          rules: ValidationRuleType.POSITIVE_DECIMAL
        },
        {
          type: ConfigInputType.TEXT,
          label: 'Buffer flow rate min. suggestion factor',
          key: 'bufferFlowRateMinSuggestionFactor',
          rules: ValidationRuleType.POSITIVE_ZERO_DECIMAL
        },
      ]
    },
    {
      title: 'Tapping classes',
      items: [
        {
          type: ConfigInputType.OBJECT,
          label: 'Tapping classes',
          key: 'tappingClasses',
          expandable: true,
          items: [
            {
              type: ConfigInputType.TEXT,
              label: 'Name',
              key: 'name',
              rules: () => ({ [ValidationRuleType.REQUIRED]: true, [ValidationRuleType.MAX]: [20] })
            },
            {
              type: ConfigInputType.TEXT,
              label: 'Min. flow rate',
              key: 'minFlowRate',
              rules: () => ({ [ValidationRuleType.POSITIVE_DECIMAL]: true })
            }
          ]
        },
      ]
    },
    {
      title: 'Building data',
      key: 'buildingData',
      items: [
        {
          type: ConfigInputType.TEXT,
          label: 'Building area ratio',
          key: '@buildingAreaRatio',
          rules: () =>
            config.value.enableHeatingLoadCalculation || config.value.enableCoolingLoadCalculation
              ? { [ValidationRuleType.REQUIRED]: true, [ValidationRuleType.POSITIVE_DECIMAL]: true }
              : { [ValidationRuleType.POSITIVE_DECIMAL]: true }
        },
      ]
    },
    {
      title: 'Construction years',
      items: [
        {
          type: ConfigInputType.OBJECT,
          key: 'constructionYears',
          label: 'Construction year',
          hideLabel: true,
          expandable: true,
          rules: () =>
            config.value.enableCoolingLoadCalculation || config.value.enableHeatingLoadCalculation
              ? { [ValidationRuleType.REQUIRED]: true }
              : {},
          items: [
            {
              label: 'Key',
              key: 'key',
              type: ConfigInputType.TEXT,
              rules: () => ({
                [ValidationRuleType.REQUIRED]: true,
                [ValidationRuleType.UNIQUE]: {
                  list: config.value.constructionYears || [],
                  key: 'key'
                }
              }),
              isUnique: true
            },
            {
              label: 'Floor',
              key: 'floor',
              type: ConfigInputType.TEXT,
              rules: [ValidationRuleType.REQUIRED, ValidationRuleType.POSITIVE_DECIMAL]
            },
            {
              label: 'Ceiling',
              key: 'ceiling',
              type: ConfigInputType.TEXT,
              rules: [ValidationRuleType.REQUIRED, ValidationRuleType.POSITIVE_DECIMAL]
            },
            {
              label: 'Wall',
              key: 'wall',
              type: ConfigInputType.TEXT,
              rules: [ValidationRuleType.REQUIRED, ValidationRuleType.POSITIVE_DECIMAL]
            },
            {
              label: 'Floor improved',
              key: 'floor_improved',
              type: ConfigInputType.TEXT,
              rules: [ValidationRuleType.REQUIRED, ValidationRuleType.POSITIVE_DECIMAL]
            },
            {
              label: 'Ceiling improved',
              key: 'ceiling_improved',
              type: ConfigInputType.TEXT,
              rules: [ValidationRuleType.REQUIRED, ValidationRuleType.POSITIVE_DECIMAL]
            },
            {
              label: 'Wall improved',
              key: 'wall_improved',
              type: ConfigInputType.TEXT,
              rules: [ValidationRuleType.REQUIRED, ValidationRuleType.POSITIVE_DECIMAL]
            },
            {
              label: 'Thermal bridge factor',
              key: 'thermal_bridge_factor',
              type: ConfigInputType.TEXT,
              rules: [ValidationRuleType.REQUIRED, ValidationRuleType.POSITIVE_DECIMAL]
            }
          ]
        }
      ]
    },
    {
      title: 'Bivalence point',
      items: [
        {
          type: ConfigInputType.TOGGLE,
          label: 'Suggest heat pumps by bivalence point in all-electric case',
          description: 'Uses bivalence point range from fuel type "ELECTRICITY".\n' +
            'Has no impact on the calculation or visibility of the bivalence point.',
          key: 'enableBivalencePointElectric'
        },
        {
          type: ConfigInputType.TOGGLE,
          label: 'Suggest heat pumps by bivalence point in hybrid case',
          description: 'Uses bivalence point range from fuel type "GAS".\n' +
            'Has no impact on the calculation or visibility of the bivalence point.',
          key: 'enableBivalencePointHybrid'
        },
        {
          type: ConfigInputType.TEXT,
          label: 'Zero-load temperature (heating)',
          key: 'zeroLoadTemperature',
          rules: [ValidationRuleType.REQUIRED, ValidationRuleType.POSITIVE_ZERO_DECIMAL]
        },
        {
          type: ConfigInputType.TEXT,
          label: 'Zero-load temperature (cooling)',
          key: 'coolingZeroLoadTemperature',
          rules: [ValidationRuleType.REQUIRED, ValidationRuleType.POSITIVE_ZERO_DECIMAL]
        },
        {
          type: ConfigInputType.TEXT,
          label: 'Flow temperature at Zero-load',
          key: 'flowTemperatureAtZeroLoad',
          rules: [ValidationRuleType.REQUIRED, ValidationRuleType.POSITIVE_ZERO_DECIMAL]
        },
        {
          type: ConfigInputType.OBJECT,
          hideLabel: true,
          key: 'hybridSystem',
          items: [
            {
              type: ConfigInputType.OBJECT,
              label: 'Climate zone',
              key: 'climateZones',
              expandable: true,
              items: [
                {
                  type: ConfigInputType.TEXT,
                  unique: true,
                  label: 'Climate zone',
                  key: 'climateZone',
                  rules: ValidationRuleType.REQUIRED,
                  isUnique: true
                },
                {
                  type: ConfigInputType.TEXT,
                  label: 'Max bivalence point',
                  key: 'maxBivalencePoint',
                  rules: [ValidationRuleType.REQUIRED, ValidationRuleType.INTEGER]
                },
                {
                  type: ConfigInputType.TEXT,
                  label: 'Min bivalence point',
                  key: 'minBivalencePoint',
                  rules: [ValidationRuleType.REQUIRED, ValidationRuleType.INTEGER]
                }
              ]
            },
            {
              type: ConfigInputType.OBJECT,
              label: 'Fuel type',
              key: 'fuelTypes',
              expandable: true,
              items: [
                {
                  type: ConfigInputType.SELECT,
                  unique: true,
                  label: 'Fuel type',
                  key: 'fuelType',
                  options: jsonSchema?.hybridSystem?.properties?.fuelTypes?.items?.properties?.fuelType?.enum,
                  rules: ValidationRuleType.REQUIRED
                },
                {
                  type: ConfigInputType.TEXT,
                  label: 'Max bivalence point',
                  key: 'maxBivalencePoint',
                  rules: ValidationRuleType.INTEGER
                },
                {
                  type: ConfigInputType.TEXT,
                  label: 'Min bivalence point',
                  key: 'minBivalencePoint',
                  rules: ValidationRuleType.INTEGER
                },
                {
                  type: ConfigInputType.TEXT,
                  label: 'DHW bivalence point',
                  key: 'dhwBivalencePoint',
                  rules: ValidationRuleType.INTEGER
                },
                {
                  type: ConfigInputType.TEXT,
                  label: 'Backup heater min. perf. factor',
                  key: 'backupHeaterMinPerformanceFactor',
                  rules: () => ({
                    [ValidationRuleType.MIN_VALUE]: [1]
                  })
                },
                {
                  type: ConfigInputType.TEXT,
                  label: 'Backup heater max. perf. factor',
                  key: 'backupHeaterMaxPerformanceFactor',
                  rules: () => ({
                    [ValidationRuleType.MIN_VALUE]: [1]
                  })
                }
              ]
            }
          ]
        }
      ]
    },
    {
      title: 'Hybrid system',
      items: [
        {
          type: ConfigInputType.TOGGLE,
          label: 'Enable hybrid system',
          key: 'enableHybridSystem'
        },
        {
          type: ConfigInputType.TEXT,
          label: 'Hybrid kit link',
          key: 'hybridKitInfoLink',
          description: 'Add hybrid product link , so the user can learn more about this product'
        },
      ]
    },
    {
      title: 'Heat pump suggestion',
      items: [
        {
          type: ConfigInputType.TOGGLE,
          label: 'Suggest heat pumps beyond operating limit',
          key: 'suggestHeatPumpsBeyondOperatingLimit',
          description: 'Suggests heat pumps beyond operating limit.'
        },
        {
          type: ConfigInputType.TEXT,
          label: 'Heat loss increase in percent beyond operating limits',
          key: 'heatLossIncreasePercentBeyondOperatingLimits',
          rules: [ValidationRuleType.REQUIRED, ValidationRuleType.INTEGER, ValidationRuleType.POSITIVE_ZERO_DECIMAL]
        },
        {
          type: ConfigInputType.TEXT,
          label: 'Suggest heat pumps by source temperature (hybrid case)',
          key: 'heatPumpSuggestionHybridSourceTemperature',
          rules: [ValidationRuleType.DECIMAL]
        },
        {
          type: ConfigInputType.TEXT,
          label: 'Suggest heat pumps by flow temperature (hybrid case)',
          key: 'heatPumpSuggestionHybridFlowTemperature',
          rules: [ValidationRuleType.INTEGER, ValidationRuleType.POSITIVE_ZERO_DECIMAL]
        },
      ]
    },
    {
      title: 'Heat pump stats',
      items: [
        {
          type: ConfigInputType.TOGGLE,
          label: 'Show operation points for heat pumps',
          key: 'showOperatingPoints',
          description: 'Shows the operation point stat for suggested heat pump (needs the stat to be present).'
        },
        {
          type: ConfigInputType.OBJECT,
          label: 'Heat pump stat',
          expandable: true,
          hideLabel: true,
          key: 'heatPumpStats',
          items: [
            {
              type: ConfigInputType.SELECT,
              key: 'heatPumpStats',
              label: 'Heat pump stat',
              isUnique: true,
              options: jsonSchema?.heatPumpStats?.items.properties.heatPumpStats.enum,
              rules: ValidationRuleType.REQUIRED
            }
          ]
        }
      ]
    },
    {
      title: 'Heat pump cascades',
      items: [
        {
          type: ConfigInputType.TOGGLE,
          label: 'Enable heat pump cascades',
          key: '@enableHeatPumpCascades'
        },
        {
          type: ConfigInputType.TOGGLE,
          label: 'Enable mixed cascade results',
          key: '@enableMixedCascadeHeatPumpResults',
          description: 'If set to true, single heat pumps and cascade results will be returned in the same list.\n' +
            'Otherwise cascades are only shown if no single heat pump covers the load.'
        },
        {
          type: ConfigInputType.TEXT,
          label: 'Max heat pump cascade size',
          key: 'maxHeatPumpCascadeSize',
          rules: () => ({
            [ValidationRuleType.MAX_VALUE]: [7],
            [ValidationRuleType.MIN_VALUE]: [0]
          })
        }, {
          type: ConfigInputType.TEXT,
          label: 'Cascade oversize limit relative to smallest suitable cascade size',
          key: 'cascadeOversizeLimit',
          rules: () => ({
            [ValidationRuleType.MAX_VALUE]: [5],
            [ValidationRuleType.MIN_VALUE]: [0]
          })
        }
      ]
    },
    {
      title: 'Energy Sources',
      items: [
        {
          type: ConfigInputType.OBJECT,
          label: 'Energy source',
          hideLabel: true,
          key: 'energySources',
          expandable: true,
          items: [
            {
              type: ConfigInputType.SELECT,
              label: 'Name',
              key: 'name',
              isUnique: true,
              options: ['air', 'ground_horizontal', 'ground_vertical', 'water'],
              rules: ValidationRuleType.REQUIRED
            },
            {
              type: ConfigInputType.TEXT,
              label: 'Source temperature',
              key: 'sourceTemperature'
            },
            {
              type: ConfigInputType.TEXT,
              label: 'Max. flow temperature',
              key: 'maxFlowTemperature',
              rules: [ValidationRuleType.INTEGER]
            },
            {
              type: ConfigInputType.SELECT,
              label: 'Value',
              key: 'value',
              options: ['air', 'ground', 'water'],
              rules: ValidationRuleType.REQUIRED
            },
            {
              type: ConfigInputType.TEXT,
              label: 'Lower range',
              key: 'lowerRange',
              rules: [ValidationRuleType.REQUIRED, ValidationRuleType.POSITIVE_ZERO_DECIMAL]
            },
            {
              type: ConfigInputType.TEXT,
              label: 'Upper range',
              key: 'upperRange',
              rules: [ValidationRuleType.REQUIRED, ValidationRuleType.POSITIVE_ZERO_DECIMAL]
            },
            {
              type: ConfigInputType.TEXT,
              label: 'Cascade min. perf. factor',
              key: 'cascadeMinPerformanceFactor',
              rules: () =>
                config.value.enableHeatPumpCascades
                  ? { [ValidationRuleType.REQUIRED]: true, [ValidationRuleType.POSITIVE_ZERO_DECIMAL]: true }
                  : { [ValidationRuleType.POSITIVE_ZERO_DECIMAL]: true }
            },
            {
              type: ConfigInputType.TEXT,
              label: 'Cascade max. perf. factor',
              key: 'cascadeMaxPerformanceFactor',
              rules: () =>
                config.value.enableHeatPumpCascades
                  ? { [ValidationRuleType.REQUIRED]: true, [ValidationRuleType.POSITIVE_ZERO_DECIMAL]: true }
                  : { [ValidationRuleType.POSITIVE_ZERO_DECIMAL]: true }
            },
            {
              type: ConfigInputType.TEXT,
              label: 'Hybrid system min. perf. factor',
              key: 'hybridSystemMinPerformanceFactor',
              rules: [ValidationRuleType.POSITIVE_ZERO_DECIMAL]
            },
            {
              type: ConfigInputType.TEXT,
              label: 'Hybrid system max. perf. factor',
              key: 'hybridSystemMaxPerformanceFactor',
              rules: [ValidationRuleType.POSITIVE_ZERO_DECIMAL]
            }
          ]
        }
      ]
    },
    {
      title: 'System types',
      items: [
        {
          type: ConfigInputType.OBJECT,
          label: 'System type',
          hideLabel: true,
          key: 'systemTypes',
          expandable: true,
          items: [
            {
              type: ConfigInputType.SELECT,
              label: 'System type',
              key: 'systemType',
              isUnique: true,
              options: jsonSchema?.systemTypes?.items?.properties?.systemType?.enum,
              rules: [ValidationRuleType.REQUIRED]
            },
            {
              type: ConfigInputType.TEXT,
              label: 'Cooling flow temperature',
              key: 'coolingFlowTemperature',
              rules: [ValidationRuleType.POSITIVE_DECIMAL]
            },
            {
              type: ConfigInputType.TEXT,
              label: 'Heating emitter efficiency',
              key: 'heatingEmitterEfficiency',
              rules: () => ({
                [ValidationRuleType.MAX_VALUE]: [1],
                [ValidationRuleType.MIN_VALUE]: [0]
              })
            },
            {
              type: ConfigInputType.TEXT,
              label: 'Heating flow temperature',
              key: 'heatingFlowTemperatures',
              expandable: true,
              rules: [ValidationRuleType.POSITIVE_DECIMAL]
            }
          ]
        } as ConfigSchemaItem
      ]
    },
    {
      title: 'Accessory groups',
      items: [
        {
          label: 'Accessory group',
          key: 'accessoryGroups',
          hideLabel: true,
          expandable: true,
          type: ConfigInputType.OBJECT,
          items: [
            {
              label: 'Group',
              key: 'group',
              type: ConfigInputType.TEXT,
              rules: () => ({
                [ValidationRuleType.REQUIRED]: true,
                [ValidationRuleType.UNIQUE]: {
                  list: config.value.accessoryGroups || [],
                  key: 'group'
                }
              }),
              isUnique: true
            },
            {
              label: 'Parent ID',
              key: 'parentIds',
              expandable: true,
              type: ConfigInputType.TEXT
            }
          ]
        }
      ]
    },
    {
      title: 'Heat pump placement',
      items: [
        {
          type: ConfigInputType.OBJECT,
          label: 'Heat pump placement',
          expandable: true,
          hideLabel: true,
          key: 'heatPumpPlacement',
          items: [
            {
              type: ConfigInputType.SELECT,
              key: 'heatPumpPlacement',
              label: 'Heat pump placement',
              isUnique: true,
              options: jsonSchema?.heatPumpPlacement?.items?.properties?.heatPumpPlacement?.enum,
              rules: ValidationRuleType.REQUIRED
            },
            {
              type: ConfigInputType.TEXT,
              key: 'correctionFactor',
              label: 'Correction factor',
              rules: [ValidationRuleType.REQUIRED, ValidationRuleType.POSITIVE_DECIMAL]
            }
          ]
        }
      ]
    },
    {
      title: 'Fuel types',
      items: [
        {
          label: 'System Electricity',
          type: ConfigInputType.OBJECT,
          key: 'systemElectricity',
          items: [
            {
              type: ConfigInputType.TEXT,
              label: 'Emission factor',
              key: 'emissionFactor',
              rules: [ValidationRuleType.REQUIRED, ValidationRuleType.POSITIVE_DECIMAL]
            },
            {
              type: ConfigInputType.TEXT,
              label: 'Price per kW/h',
              key: 'price',
              rules: [ValidationRuleType.REQUIRED, ValidationRuleType.POSITIVE_DECIMAL]
            }
          ]
        },
        {
          label: 'System Gas',
          type: ConfigInputType.OBJECT,
          key: 'systemGas',
          items: [
            {
              type: ConfigInputType.TEXT,
              label: 'Emission factor',
              key: 'emissionFactor',
              rules: [ValidationRuleType.REQUIRED, ValidationRuleType.POSITIVE_DECIMAL]
            },
            {
              type: ConfigInputType.TEXT,
              label: 'Price per kW/h',
              key: 'price',
              rules: [ValidationRuleType.REQUIRED, ValidationRuleType.POSITIVE_DECIMAL]
            }
          ]
        },

        {
          type: ConfigInputType.OBJECT,
          key: 'referenceFuelTypes',
          expandable: true,
          items: [
            {
              type: ConfigInputType.SELECT,
              isUnique: true,
              label: 'Fuel type',
              key: 'fuelType',
              options: jsonSchema?.referenceFuelTypes?.items.properties.fuelType.enum,
              rules: [ValidationRuleType.REQUIRED]
            },
            {
              type: ConfigInputType.TEXT,
              label: 'Heating efficiency',
              key: 'heatingEfficiency',
              rules: () => ({
                [ValidationRuleType.MAX_VALUE]: [1],
                [ValidationRuleType.MIN_VALUE]: [0]
              })
            },
            {
              type: ConfigInputType.OBJECT,
              label: 'Calorific value',
              key: 'calorificValues',
              expandable: true,
              items: [
                {
                  type: ConfigInputType.TEXT,
                  label: 'Unit',
                  key: 'unit',
                  rules: [ValidationRuleType.REQUIRED]
                },
                {
                  type: ConfigInputType.TEXT,
                  label: 'Calorific value',
                  key: 'calorificValue',
                  rules: [ValidationRuleType.REQUIRED, ValidationRuleType.POSITIVE_DECIMAL]
                }
              ]
            },
            {
              type: ConfigInputType.TEXT,
              label: 'Emission factor',
              key: 'emissionFactor',
              rules: [ValidationRuleType.REQUIRED, ValidationRuleType.POSITIVE_DECIMAL]
            },
            {
              type: ConfigInputType.TEXT,
              label: 'Price per kW/h',
              key: 'price',
              rules: [ValidationRuleType.REQUIRED, ValidationRuleType.POSITIVE_DECIMAL]
            }
          ]
        }
      ]
    },
    {
      title: 'Default behavior',
      items: [
        {
          type: ConfigInputType.TOGGLE,
          label: '230V',
          description: 'Heat pump voltage filter 230V',
          key: 'preselectHeatPumpVoltageFilter230V',
        },
        {
          type: ConfigInputType.TOGGLE,
          label: '400V',
          description: 'Heat pump voltage filter 400V',
          key: 'preselectHeatPumpVoltageFilter400V',
        },
        {
          type: ConfigInputType.TOGGLE,
          label: 'Existing combi boiler',
          description: 'Existing backup heater supports DHW',
          key: 'preselectExistingBackupHeaterCombiBoiler',
        }
      ]
    },
    {
      title: 'System schemes',
      items: [
        {
          type: ConfigInputType.TOGGLE,
          label: 'Enable international system schemes',
          key: 'enableInternationalSystemSchemes',
          description: 'Show international system schemes along with localized ones.'
        },
        {
          type: ConfigInputType.TOGGLE,
          label: 'Enable request schemes',
          key: 'enableRequestSystemScheme',
          description: 'Allows requesting a system scheme via email in case none was found.'
        },
        {
          type: ConfigInputType.TEXT,
          label: 'System scheme min. score in %',
          key: 'minSystemSchemeScorePercent',
          rules: [ValidationRuleType.REQUIRED, ValidationRuleType.POSITIVE_DECIMAL]
        },
        {
          type: ConfigInputType.TEXT,
          key: 'requestSystemSchemeEmail',
          label: 'Request system scheme email',
          rules: ValidationRuleType.EMAIL
        },
      ]
    },
    {
      title: 'Other',
      items: [
        {
          type: ConfigInputType.TEXT,
          label: 'Car emission factor',
          key: 'carEmissionFactor',
          rules: [ValidationRuleType.REQUIRED, ValidationRuleType.POSITIVE_DECIMAL]
        },
        {
          type: ConfigInputType.TEXT,
          label: 'Car distance conversion factor',
          key: 'carDistanceConversionFactor',
          rules: [ValidationRuleType.REQUIRED, ValidationRuleType.POSITIVE_DECIMAL]
        },
        {
          type: ConfigInputType.TEXT,
          label: 'Postal code validation pattern',
          key: 'postalCodeValidationPattern'
        },
        {
          type: ConfigInputType.TEXT,
          label: 'Postal code normalization pattern',
          key: 'postalCodeNormalizationPattern'
        },
        {
          type: ConfigInputType.TEXT,
          label: 'External link URL (heat loss field)',
          key: 'heatLossExternalLinkUrl'
        },
        {
          type: ConfigInputType.OBJECT,
          label: 'Global links',
          key: 'globalLinks',
          items: [
            {
              type: ConfigInputType.TEXT,
              label: 'Imprint',
              key: 'imprint',
              rules: ValidationRuleType.REQUIRED
            },
            {
              type: ConfigInputType.TEXT,
              label: 'Data protection',
              key: 'dataProtection',
              rules: ValidationRuleType.REQUIRED
            }
          ]
        },
        {
          type: ConfigInputType.OBJECT,
          label: 'Product availability links',
          key: 'productAvailabilityLinks',
          items: [
            {
              type: ConfigInputType.TEXT,
              label: 'Backup heaters',
              key: 'backupHeaters',
            },
            {
              type: ConfigInputType.TEXT,
              label: 'DHW tanks',
              key: 'dhwTanks',
            },
            {
              type: ConfigInputType.TEXT,
              label: 'Buffer tanks',
              key: 'bufferTanks',
            },
            {
              type: ConfigInputType.TEXT,
              label: 'Heat pumps',
              key: 'heatPumps',
            }
          ]
        },
        {
          type: ConfigInputType.TEXT,
          key: 'nationalNoiseLimitation',
          label: 'National noise limitation',
          rules: ValidationRuleType.POSITIVE_DECIMAL
        },
        {
          type: ConfigInputType.TEXT,
          key: 'soundSensitivityCorrection',
          label: 'Sound sensitivity correction',
          rules: ValidationRuleType.DECIMAL
        },
        {
          type: ConfigInputType.TEXT,
          label: 'Fallback indoor temperature',
          key: 'fallbackIndoorTemperature',
          rules: () => ({ [ValidationRuleType.POSITIVE_DECIMAL]: true })
        },
        {
          type: ConfigInputType.TEXT,
          label: 'Maximum drilling depth',
          key: 'maxDrillingDepth',
          rules: () => ({ [ValidationRuleType.POSITIVE_DECIMAL]: true })
        },
        {
          type: ConfigInputType.OBJECT,
          label: 'Ground type',
          key: 'groundTypes',
          expandable: true,
          items: [
            {
              type: ConfigInputType.TEXT,
              isUnique: true,
              label: 'Ground type',
              key: 'name',
              rules: ValidationRuleType.REQUIRED
            },
            {
              type: ConfigInputType.TEXT,
              isUnique: false,
              label: 'Withdrawal power',
              key: 'withdrawalPower',
              rules: [ValidationRuleType.REQUIRED, ValidationRuleType.POSITIVE_DECIMAL]
            }
          ]
        },
        {
          type: ConfigInputType.TEXT,
          label: 'Feedback Survey link',
          key: 'feedbackSurveyLink',
          description: 'Add the survey link without country code at the end. Language it will be automatically added.(example for country code: EN)'
        },
        {
          type: ConfigInputType.TEXT,
          label: 'Feedback survey frequency',
          key: 'feedbackSurveyFrequency',
          description: 'Add the frequency for the survey',
          rules: [ValidationRuleType.INTEGER]
        },
      ]
    }
  ]
}
</script>

<template>
  <div>
    <PageHeader />

    <div class="main-wrapper">
      <div class="container main">
        <div class="config-wrapper config-scope">
          <div class="section-nav-wrapper">
            <div class="section-nav card">
              <div class="section-nav__header">
                Configuration for<br>
                {{ brand }} / {{ country }}
              </div>
              <div class="section-nav__navigation">
                <div id="section-nav-marker" class="section-nav__marker" />
                <ul>
                  <li
                    v-for="(section, index) in sections"
                    :id="'section-nav-' + camelize(section)"
                    :key="section + index"
                    @click="sectionNavItemClicked(section)"
                  >
                    {{ section }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div class="config-sections-container">
            <section
              v-for="(section, index) in configSchema"
              :id="'section-' + camelize(section.title)"
              :key="'schema-section-' + index"
              :data-section="camelize(section.title)"
            >
              <div class="form">
                <h2 class="card__title">
                  {{ section.title }}
                </h2>
                <div class="card">
                  <div class="form__section has--line">
                    <div class="form__section-inner">
                      <div class="row form__row">
                        <ConfigElement
                          v-for="(item, i) in section.items"
                          :key="item.key + i"
                          ref="configElementComponent"
                          :config="config"
                          :options="item.options"
                          :parent-keys="section.key ? [section.key] : undefined"
                          :repeatable="item.expandable"
                          :schema-item="item"
                          :validator="validator"
                          @delete="deleteClicked"
                          @disable="disableClicked"
                          @editkey="editClicked"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
    <div class="config__controls config-controls">
      <div class="container">
        <button class="btn is--secondary config-controls__reset" @click="resetBtnClicked">
          Reset changes
        </button>
        <button class="btn config-controls__save" @click="saveBtnClicked">
          Save changes
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.card__title {
  display: flex;
  justify-content: space-between;
  margin-bottom: rem-calc(16);

  &:not(:first-of-type) {
    margin-top: rem-calc(32);
  }
}

.form {
  .card {
    overflow: visible;
    margin-bottom: rem-calc(32);
  }
}

.config-scope {
  .form {
    &__section-inner {
      & > .form__row:first-child {
        & > .config__object-array {
          &:first-child {
            border-top: none;
            padding-top: 0;

            &:before {
              content: none;
            }
          }

          &:last-child {
            padding-bottom: 0;
          }
        }
      }
    }
  }

  .form__row:last-child {
    & > *:last-child {
      padding-bottom: 0;

      & > .config__array-item:last-of-type {
        & > .config__object:last-child {
          border-bottom: none;
          padding-bottom: 0;
        }
      }
    }
  }
}
</style>
